import type { GetGroupWhiteboardsQuery, GetLessonWhiteboardQuery } from 'core/graphql';

export interface IWhiteboardLessonRef {
  lessonId: string;
  lessonName: string | null;
  lessonNumber: number | null;
  lessonDate: string | null;
}

/**
 * Parsed excalidraw scene (the serializeAsJSON payload). Fields are kept
 * loosely typed — the excalidraw-specific element/appState types live at the
 * canvas boundary, not in the data layer.
 */
export interface IWhiteboardScene {
  elements: readonly unknown[];
  appState: Record<string, unknown> | null;
  files: Record<string, unknown> | null;
}

/**
 * A scene image stored on S3 (spec 0010 rev.3). `url` is a short-TTL presigned
 * GET link — fetch it into a dataURL and hand it to excalidraw's addFiles;
 * never persist it.
 */
export interface IWhiteboardFile {
  fileId: string;
  mimeType: string | null;
  url: string;
}

export interface IWhiteboard {
  id: string;
  name: string;
  sceneVersion: number;
  createdAt: string;
  updatedAt: string;
  /** null when the board is empty or the stored JSON failed to parse. */
  scene: IWhiteboardScene | null;
  lessons: IWhiteboardLessonRef[];
  /**
   * S3-hosted scene images. Empty for boards saved before rev.3 — those still
   * carry their images inline in `scene.files`, which keeps working.
   */
  files: IWhiteboardFile[];
}

export interface IWhiteboardMeta {
  id: string;
  name: string;
  sceneVersion: number;
  updatedAt: string;
  lessons: IWhiteboardLessonRef[];
}

type RawLessonRef = {
  lessonId: string;
  lessonName?: string | null;
  lessonNumber?: number | null;
  lessonDate?: string | null;
};

const transformLessonRef = (ref: RawLessonRef): IWhiteboardLessonRef => ({
  lessonId: ref.lessonId,
  lessonName: ref.lessonName ?? null,
  lessonNumber: ref.lessonNumber ?? null,
  lessonDate: ref.lessonDate ?? null,
});

/**
 * JSON.parse the stored sceneJson, guarded: a corrupt payload must degrade to
 * an empty board, never crash the lesson page.
 */
export const parseWhiteboardScene = (
  sceneJson: string | null | undefined,
): IWhiteboardScene | null => {
  if (!sceneJson) {
    return null;
  }
  try {
    const parsed = JSON.parse(sceneJson);
    if (!parsed || typeof parsed !== 'object') {
      return null;
    }
    const appState =
      parsed.appState && typeof parsed.appState === 'object' ? { ...parsed.appState } : null;
    if (appState) {
      // excalidraw expects appState.collaborators to be a Map; a JSON-roundtripped
      // plain object breaks restore — drop it defensively.
      delete appState.collaborators;
    }
    return {
      elements: Array.isArray(parsed.elements) ? parsed.elements : [],
      appState,
      files: parsed.files && typeof parsed.files === 'object' ? parsed.files : null,
    };
  } catch {
    return null;
  }
};

export const transformWhiteboard = (
  data: GetLessonWhiteboardQuery | undefined,
): IWhiteboard | null => {
  const board = data?.lessonWhiteboard;
  if (!board) {
    return null;
  }
  return {
    id: board.id,
    name: board.name,
    sceneVersion: board.sceneVersion,
    createdAt: board.createdAt,
    updatedAt: board.updatedAt,
    scene: parseWhiteboardScene(board.sceneJson),
    lessons: board.lessons.map(transformLessonRef),
    files: (board.files ?? []).map((file) => ({
      fileId: file.fileId,
      mimeType: file.mimeType ?? null,
      url: file.url,
    })),
  };
};

export const transformGroupWhiteboards = (
  data: GetGroupWhiteboardsQuery | undefined,
): IWhiteboardMeta[] => {
  const boards = (data?.groupWhiteboards ?? []).map((board) => ({
    id: board.id,
    name: board.name,
    sceneVersion: board.sceneVersion,
    updatedAt: board.updatedAt as string,
    lessons: board.lessons.map(transformLessonRef),
  }));
  // Backend already sorts by -updated_at; sort defensively so the "most
  // recent preselected" contract of the continue picker holds regardless.
  return boards.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
};
